import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { plainToClass, Type } from 'class-transformer';
import * as jsonpath from 'jsonpath';
import { get, maxBy, minBy } from 'lodash';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { LangService } from 'src/app/common/service/lang.service';
import { PEOPLE_ENTER_COLUMN_NAME, PEOPLE_EXIT_COLUMN_NAME, StopPileStats } from 'src/app/reports/model/stop-pile-stats';
import { FieldType } from 'src/app/settings/model/field-type';
import { ValueField } from 'src/app/settings/model/value-field';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { ErrorType } from 'src/app/shared/model/error-type';
import { FileType } from 'src/app/shared/model/file-type';
import { ViewState } from 'src/app/shared/model/view-state';
import { timeWithSecondsFormat, visibleDateTimeFormat } from 'src/app/shared/util/date-util';
import { createTitlesAsString, createViewStateAsString } from 'src/app/shared/util/view-state-creator';
import { ChartType } from '../../shared/model/chart-type';
import { BarDatum, BarLayout, PlotlyBarInput } from '../../shared/model/plotly-bar-input';
import { ChartConfig, ModeBarClick, plotlyDefaultIcons } from '../../shared/model/plotly-chart-config';
import { PieDatum, PieLayout, PlotlyPieInput } from '../../shared/model/plotly-pie-input';
import { RequestCriteria } from '../../shared/model/request-criteria';
import { RestSimpleRequest } from '../../shared/model/rest-simple-request';
import { RestService } from '../../shared/service/rest.service';
import { createChartTitle, createPassengerReportFilename, generateReportTitle } from '../util/bus-stop-report-util';
import { SortBy } from 'src/app/shared/model/data-source';

@Injectable()
export class PeopleCountersChartDataPreparatorService extends RestService {
  private readonly green = 'rgb(67, 206, 41)';
  private readonly red = 'rgb(246, 0, 0)';

  constructor(http: HttpClient, private translate: TranslateService, private langService: LangService, private settings: UserSettingsService) {
    super(http);
  }

  public generateChartData(filters: RequestCriteria, sortBy: SortBy[], groupBy: string, fieldsToShow: string[],
    correctedReportsDataType: boolean | undefined, filename: string):
    Observable<ChartJson> {
    const reportTitle = generateReportTitle(filters, this.translate.instant('busStopReports'), this.translate);
    const titles = createTitlesAsString(fieldsToShow, this.translate);
    const viewState = createViewStateAsString(sortBy, fieldsToShow, groupBy, { correctedReportsDataType });
    const fileName = filename || createPassengerReportFilename('charts_passengers_flow', correctedReportsDataType, filters, this.translate);

    const request: RestSimpleRequest<RequestCriteria> = {
      params: {
        viewState,
        reportTitle,
        titles,
        file_type: FileType.CHART,
        filename: fileName,
        summaries: '{}',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Accept-Language': this.langService.getLanguage()
      },
      data: filters
    };
    return this.postAndExtractSingle<ChartJson>(ChartJson, 'api/chart/criteria/fetch', request);
  }


  public getChartDataAsPlotlyInput(chartJson: ChartJson, fileId: number, chartType: ChartType, filters: RequestCriteria, viewState: ViewState,
    fieldsForLabels: ValueField[], selectLabelFieldsClick: ModeBarClick): Observable<PlotlyBarInput | PlotlyPieInput> {
    if (chartJson)
      return of(chartJson).pipe(
        map(data => this.convertToPlotlyInput(chartType, data, filters, viewState, fieldsForLabels, selectLabelFieldsClick))
      );
    else
      return this.fetchDataFromFile(fileId).pipe(
        map(data => this.convertToPlotlyInput(chartType, data, data.criteria, data.viewState, null, selectLabelFieldsClick))
      );
  }

  private fetchDataFromFile(fileId: number): Observable<StpChartJson> {
    return this.doGet<any>(`files/download/${fileId}`)
      .pipe(map(content => plainToClass(StpChartJson, content)));
  }

  private convertToPlotlyInput(chartType: ChartType, fromJson: ChartJson, filters: RequestCriteria, viewState: ViewState, fieldsForLabels: ValueField[],
    selectLabelFieldsClick: ModeBarClick): PlotlyBarInput | PlotlyPieInput {
    if (fromJson == null || fromJson.x.length == 0) throw new Error(ErrorType.NO_DATA_TO_SHOW);

    const reportNameKey = this.settings.getBusStopReportNameOptionSetting();
    const reportTitle = createChartTitle(reportNameKey, filters, viewState.additional?.correctedReportsDataType, this.translate);
    const yAxisTitle = fromJson.yAxisTitle;
    let x = fromJson.x;
    const xObjects = fromJson.xObjects;
    const enterValues = fromJson.enterValues;
    const exitValues = fromJson.exitValues;
    const countValues = fromJson.countValues;

    const config: ChartConfig = {
      locale: this.langService.getLanguage(),
      modeBarButtonsToRemove: ['sendDataToCloud', 'select2d', 'lasso2d', 'toggleSpikelines', 'hoverClosestCartesian', 'hoverClosestPie',
        'hoverCompareCartesian'],
      toImageButtonOptions: { filename: reportTitle.replace(/<br>/g, '') }
    };
    if (xObjects && selectLabelFieldsClick) {
      config.modeBarButtonsToAdd = [{
        name: this.translate.instant('selectFieldsOnLabels'),
        icon: plotlyDefaultIcons.pencil,
        click: () => selectLabelFieldsClick(fromJson)
      }];
    }

    x = this.createLabels(x, xObjects, fieldsForLabels);

    if (chartType == ChartType.PASSENGER_COUNTERS_BAR) {
      const generatedInput = this.generateForBar(yAxisTitle, x, countValues, enterValues, exitValues, reportTitle, viewState);
      return {
        data: generatedInput.data,
        layout: generatedInput.layout,
        config
      } as PlotlyBarInput;

    } else if (chartType == ChartType.PASSENGER_COUNTERS_PIE) {
      const generatedInput = this.generateForPie(yAxisTitle, x, countValues, enterValues, exitValues, reportTitle);
      return {
        data: generatedInput.data,
        layout: generatedInput.layout,
        config
      } as PlotlyPieInput;

    } else {
      console.error(`Unrecognized char type ${chartType}`);
      return { data: [] };
    }
  }

  private generateForBar(yAxisTitle: string, x: string[], countValues: number[], enterValues: number[], exitValues: number[],
    reportTitle: string, viewState: ViewState): { layout: BarLayout; data: BarDatum[] } {
    if (!yAxisTitle)
      yAxisTitle = this.translate.instant('people_count');

    const data = [];
    if (countValues) {
      data.push({
        x,
        y: countValues,
        type: 'bar',
        name: this.translate.instant('people_in_vehicle')
      });
    }
    if (enterValues) {
      data.push({
        x,
        y: enterValues,
        type: 'bar',
        name: this.translate.instant(PEOPLE_ENTER_COLUMN_NAME),
        marker: {
          color: this.green
        }
      });
    }
    if (exitValues) {
      data.push({
        x,
        y: exitValues,
        type: 'bar',
        name: this.translate.instant(PEOPLE_EXIT_COLUMN_NAME),
        marker: {
          color: this.red
        }
      });
    }

    const groupName = viewState.groups ? viewState.groups[0].fieldName : '';
    const layout: BarLayout = {
      title: reportTitle,
      xaxis: {
        title: {
          text: groupName != '' ? this.translate.instant(groupName) : '',
          standoff: 20
        },
        tickangle: 90,
        automargin: true
      },
      yaxis: {
        title: yAxisTitle
      },
      height: this.getChartHeight(),
      barmode: 'group',
      showlegend: true
    };

    return { layout, data };
  }

  private generateForPie(yAxisTitle: string, x: string[], countValues: number[], enterValues: number[], exitValues: number[],
    reportTitle: string): { layout: PieLayout; data: PieDatum[] } {
    if (!yAxisTitle)
      yAxisTitle = this.translate.instant('people_count');

    const columnOrderStack = [2, 1, 0];
    const data = [];

    const annotations = [];
    let chartsXCentersStack: number[] = [];

    let numberOfCountersToShow = 0;
    if (enterValues?.length > 0)
      numberOfCountersToShow++;
    if (exitValues?.length > 0)
      numberOfCountersToShow++;
    if (countValues?.length > 0)
      numberOfCountersToShow++;

    switch (numberOfCountersToShow) {
      case 3:
        chartsXCentersStack = [0.88, 0.50, 0.12];
        break;
      case 2:
        chartsXCentersStack = [0.79, 0.21];
        break;
      case 1:
        chartsXCentersStack = [0.50];
        break;
    }

    if (countValues?.length > 0)
      this.fillDataAndAnnotations(data, countValues, x, columnOrderStack, annotations, yAxisTitle, chartsXCentersStack);

    if (enterValues?.length > 0)
      this.fillDataAndAnnotations(data, enterValues, x, columnOrderStack, annotations, PEOPLE_ENTER_COLUMN_NAME, chartsXCentersStack);

    if (exitValues?.length > 0)
      this.fillDataAndAnnotations(data, exitValues, x, columnOrderStack, annotations, PEOPLE_EXIT_COLUMN_NAME, chartsXCentersStack);

    const layout: PieLayout = {
      title: reportTitle,
      height: this.getChartHeight(),
      annotations,
      grid: { rows: 1, columns: numberOfCountersToShow }
    };
    return { layout, data };
  }

  private fillDataAndAnnotations(
    data: any[], yValues: number[], x: string[], columnOrderStack: number[], annotations: any[], annotation: string, chartsXCentersStack: number[]
  ): void {
    data.push({
      values: yValues,
      labels: x,
      sort: false,
      type: 'pie',
      domain: {
        row: 0,
        column: columnOrderStack.pop()
      },
      hoverinfo: 'label+value',
      textinfo: 'none'
    });
    annotations.push({
      font: {
        size: 15
      },
      showarrow: false,
      text: this.translate.instant(annotation),
      x: chartsXCentersStack.pop(),
      y: 0.0
    });
  }

  private createLabels(x: string[], xObjects: any[], fieldsForLabels: ValueField[]): string[] {
    if (fieldsForLabels) {
      const dateFormat = this.getDateFormat(xObjects, fieldsForLabels);

      x = xObjects.map(data => {
        const label = [];
        fieldsForLabels.forEach(field => {
          let value = this.getValueForField(data, field);
          if (value && field.type == FieldType.DATETIME) {
            if (dateFormat)
              value = moment(value).format(dateFormat);
            else
              value = moment(value).toVisibleDateTimeFormat();
          }
          if (value)
            label.push(value);
        });
        return label.join(', ');
      });
    }
    return x;
  }

  private getValueForField(data: any[], field: ValueField): any {
    if (field.jsonpath)
      return jsonpath.value(data, field.jsonpath);
    else
      return get(data, field.fieldName || field.name);
  }

  private getDateFormat(xObjects: any[], fieldsForLabels: ValueField[]): string | null {
    const dateField = fieldsForLabels.find(field => field.type == FieldType.DATETIME);
    if (dateField) {
      const minDate = minBy(xObjects, data => this.getValueForField(data, dateField));
      const maxDate = maxBy(xObjects, data => this.getValueForField(data, dateField));
      if (minDate && maxDate) {
        const isTheSameDay = moment(minDate).isSame(moment(maxDate), 'day');
        if (isTheSameDay)
          return timeWithSecondsFormat;
        else
          return visibleDateTimeFormat;
      }
    }
    return null;
  }

  private getChartHeight(): number {
    return window.innerHeight - 130; // to fit chart inside dialog
  }
}

export class ChartJson {
  yAxisTitle: string;
  x: string[];
  @Type(() => StopPileStats)
  xObjects: StopPileStats[];
  countValues: number[];
  enterValues: number[];
  exitValues: number[];
}

export class StpChartJson extends ChartJson {
  criteria: RequestCriteria;
  @Type(() => ViewState)
  viewState: ViewState;
}
